import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { Dialog } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { isNotNil } from "client/Misc/utils";
import { HubDetailsDialogContent, HubDetailsDialogContentHandle } from "./HubDetailsDialogContent";
import { HubItemViewLocation } from "client/Api/Hub/common.details";
import { useOpenDialog } from "client/Hooks";
import { hubItemKindToLocalizationKey } from "./Overview/Overview";

export type HubDetailsDialogProps = {
	id: number;
	itemKind: HubItemKind;
	commentId?: number;
	onClose: () => void;
};

export const HubDetailsDialog = (props: HubDetailsDialogProps): ReactElement => {
	const {
		onClose,
		...contentProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const contentRef: RefObject<HubDetailsDialogContentHandle | null> = useRef<HubDetailsDialogContentHandle>(null);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (isNotNil(contentRef.current)) {
				const shouldStay: boolean = await contentRef.current
					.beforeCloseHandler();

				if (shouldStay) {
					return;
				}
			}

			onClose();
		},
		[onClose]
	);

	return (
		<Dialog
			trackingId="hub-detail-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={closeHandler}
		>
			<Dialog.Header
				hasBorder
				title={localize(hubItemKindToLocalizationKey[contentProps.itemKind])}
				onClose={closeHandler}
			/>
			<Dialog.Body goesEdgeToEdge>
				<HubDetailsDialogContent
					ref={contentRef}
					{...contentProps}
					onIsBusyChange={setIsBusy}
					viewLocation={HubItemViewLocation.Dialog}
				/>
			</Dialog.Body>
			<Dialog.Footer/>
		</Dialog>
	);
};
HubDetailsDialog.displayName = "HubDetailsDialog";

export type HubDetailsDialogOpenData = OmitDistributive<HubDetailsDialogProps, "onClose">;

export function useHubDetailsDialog(): [
	element: ReactElement | null,
	open: (openData: HubDetailsDialogOpenData) => Promise<void>
] {
	return useOpenDialog<HubDetailsDialogOpenData>(
		(openData, onClose) => (
			<HubDetailsDialog
				{...openData}
				onClose={onClose}
			/>
		)
	);
}